import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './SearchResult.css'; // Assuming you want to use the same CSS

const SavedPlaylists = () => {
  const [savedPlaylists, setSavedPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [showPlaylistView, setShowPlaylistView] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadSavedPlaylists();
  }, []);

  const loadSavedPlaylists = () => {
    try {
      const playlists = JSON.parse(localStorage.getItem('savedPlaylists') || '[]');
      setSavedPlaylists(playlists);
    } catch (error) {
      console.error('Error loading saved playlists:', error);
      setSavedPlaylists([]);
    }
  };

  const handlePlaylistClick = (playlist) => {
    setSelectedPlaylist(playlist);
    setShowPlaylistView(true);
  };

  const handleBackToList = () => {
    setShowPlaylistView(false);
    setSelectedPlaylist(null);
  };

  const handleDeletePlaylist = (playlistId, event) => {
    event.stopPropagation(); // Prevent triggering the playlist click
    
    if (window.confirm('Are you sure you want to delete this playlist?')) {
      const updatedPlaylists = savedPlaylists.filter(playlist => playlist.id !== playlistId);
      localStorage.setItem('savedPlaylists', JSON.stringify(updatedPlaylists));
      setSavedPlaylists(updatedPlaylists);
      
      // If we're currently viewing the deleted playlist, go back to list
      if (selectedPlaylist && selectedPlaylist.id === playlistId) {
        handleBackToList();
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const navigateToVideo = (videoId, playlistId) => {
    navigate(`/player/${playlistId}/${videoId}`);
  };

  // Get thumbnails for gallery effect
  const getPlaylistThumbnails = (playlist) => {
    const thumbnails = [];
    if (playlist.items && playlist.items.length > 0) {
      // Get up to 9 thumbnails for the gallery effect
      for (let i = 0; i < Math.min(9, playlist.items.length); i++) {
        if (playlist.items[i]?.snippet?.thumbnails?.medium?.url) {
          thumbnails.push({
            url: playlist.items[i].snippet.thumbnails.medium.url,
            title: playlist.items[i].snippet.title
          });
        }
      }
    }
    return thumbnails;
  };

  // If showing playlist view, render the selected playlist's videos
  if (showPlaylistView && selectedPlaylist) {
    return (
      <div>
        <br />
        <br />
        <br />
        
        <div className="container">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <button 
              className="btn btn-outline-primary"
              onClick={handleBackToList}
            >
              ← Back to Saved Playlists
            </button>
            <h4 className="text-center flex-grow-1 mb-0">{selectedPlaylist.name}</h4>
            <div style={{ width: '140px' }}></div> {/* Spacer for centering */}
          </div>
          
          {selectedPlaylist.description && (
            <div className="text-center mb-4">
              <p className="text-muted">{selectedPlaylist.description}</p>
            </div>
          )}
          
          <div className="text-center mb-3">
            <small className="text-muted">
              {selectedPlaylist.items.length} videos • Saved on {formatDate(selectedPlaylist.savedAt)}
            </small>
          </div>
          
          <hr />
          
          <div className="row">
            {selectedPlaylist.items.map((item, index) => (
              <div key={`${item.snippet.resourceId.videoId}-${index}`} className="col-lg-4 col-md-6 col-sm-12 mb-4">
                <div className="card">
                  <div 
                    onClick={() => navigateToVideo(item.snippet.resourceId.videoId, selectedPlaylist.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <img
                      src={item.snippet.thumbnails.medium.url}
                      className="card-img-top"
                      alt={item.snippet.title}
                    />
                  </div>
                  <div className="card-body">
                    <div 
                      onClick={() => navigateToVideo(item.snippet.resourceId.videoId, selectedPlaylist.id)}
                      style={{ cursor: 'pointer', textDecoration: 'none' }}
                      className="maincard1"
                    >
                      <h5 className="card-title">{item.snippet.title}</h5>
                    </div>
                    <p className="font-weight-bold">{item.snippet.channelTitle}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <br />
        <br />
      </div>
    );
  }

  // Default view: Show list of saved playlists with gallery design
  return (
    <div>
      <br />
      <br />
      <br />
      
      <h4 className="text-center text">Saved Playlists</h4>
      <hr />
      
      <div className="container">
        {savedPlaylists.length === 0 ? (
          <div className="text-center">
            <div style={{ fontSize: '4rem', color: '#ddd', marginBottom: '1rem' }}>📁</div>
            <p className="text-muted">No saved playlists found.</p>
            <p className="text-muted">Save playlists to view them here.</p>
          </div>
        ) : (
          <div className="row">
            {savedPlaylists.map((playlist) => {
              const thumbnails = getPlaylistThumbnails(playlist);
              const videoCount = playlist.items?.length || 0;
              
              return (
                <div key={playlist.id} className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-4">
                  <div 
                    className="card h-100 playlist-gallery-card"
                    onClick={() => handlePlaylistClick(playlist)}
                    style={{ 
                      cursor: 'pointer', 
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: 'none',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                      background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
                      e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0) scale(1)';
                      e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
                    }}
                  >
                    {/* Gallery-style thumbnail section */}
                    <div style={{ 
                      position: 'relative', 
                      height: '220px', 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {thumbnails.length > 0 ? (
                        <div style={{ 
                          position: 'relative', 
                          width: '100%', 
                          height: '100%',
                          padding: '16px'
                        }}>
                          {thumbnails.length === 1 ? (
                            // Single thumbnail with overlay effect
                            <div style={{
                              width: '100%',
                              height: '100%',
                              borderRadius: '12px',
                              overflow: 'hidden',
                              position: 'relative',
                              boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
                            }}>
                              <img
                                src={thumbnails[0].url}
                                alt={thumbnails[0].title}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                              />
                              <div style={{
                                position: 'absolute',
                                inset: 0,
                                background: 'linear-gradient(45deg, rgba(0,0,0,0.4) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)'
                              }} />
                              <div style={{
                                position: 'absolute',
                                bottom: '12px',
                                right: '12px',
                                background: 'rgba(0,0,0,0.8)',
                                backdropFilter: 'blur(10px)',
                                color: 'white',
                                padding: '6px 12px',
                                borderRadius: '20px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}>
                                <span>🎬</span>
                                {videoCount} videos
                              </div>
                            </div>
                          ) : thumbnails.length <= 4 ? (
                            // 2x2 grid for 2-4 thumbnails
                            <div style={{
                              width: '100%',
                              height: '100%',
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gridTemplateRows: '1fr 1fr',
                              gap: '8px'
                            }}>
                              {Array.from({ length: 4 }).map((_, index) => {
                                const thumbnail = thumbnails[index];
                                return (
                                  <div
                                    key={index}
                                    style={{
                                      borderRadius: '8px',
                                      overflow: 'hidden',
                                      position: 'relative',
                                      background: thumbnail ? 'transparent' : 'rgba(255,255,255,0.1)',
                                      border: thumbnail ? 'none' : '2px dashed rgba(255,255,255,0.3)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      boxShadow: thumbnail ? '0 4px 16px rgba(0,0,0,0.2)' : 'none'
                                    }}
                                  >
                                    {thumbnail ? (
                                      <>
                                        <img
                                          src={thumbnail.url}
                                          alt={thumbnail.title}
                                          style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                          }}
                                        />
                                        {index === 3 && videoCount > 4 && (
                                          <div style={{
                                            position: 'absolute',
                                            inset: 0,
                                            background: 'rgba(0,0,0,0.7)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            fontSize: '14px',
                                            fontWeight: 'bold'
                                          }}>
                                            +{videoCount - 3}
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <div style={{
                                        color: 'rgba(255,255,255,0.5)',
                                        fontSize: '16px'
                                      }}>
                                        📹
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            // 3x3 grid for 5+ thumbnails
                            <div style={{
                              width: '100%',
                              height: '100%',
                              display: 'grid',
                              gridTemplateColumns: 'repeat(3, 1fr)',
                              gridTemplateRows: 'repeat(3, 1fr)',
                              gap: '6px'
                            }}>
                              {Array.from({ length: 9 }).map((_, index) => {
                                const thumbnail = thumbnails[index];
                                return (
                                  <div
                                    key={index}
                                    style={{
                                      borderRadius: '6px',
                                      overflow: 'hidden',
                                      position: 'relative',
                                      background: thumbnail ? 'transparent' : 'rgba(255,255,255,0.08)',
                                      border: thumbnail ? 'none' : '1px dashed rgba(255,255,255,0.2)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      boxShadow: thumbnail ? '0 2px 8px rgba(0,0,0,0.2)' : 'none'
                                    }}
                                  >
                                    {thumbnail ? (
                                      <>
                                        <img
                                          src={thumbnail.url}
                                          alt={thumbnail.title}
                                          style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                          }}
                                        />
                                        {index === 8 && videoCount > 9 && (
                                          <div style={{
                                            position: 'absolute',
                                            inset: 0,
                                            background: 'rgba(0,0,0,0.8)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            fontSize: '10px',
                                            fontWeight: 'bold'
                                          }}>
                                            +{videoCount - 8}
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <div style={{
                                        color: 'rgba(255,255,255,0.3)',
                                        fontSize: '12px'
                                      }}>
                                        📹
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          
                          {/* Floating video count badge */}
                          <div style={{
                            position: 'absolute',
                            top: '20px',
                            left: '20px',
                            background: 'rgba(0,0,0,0.8)',
                            backdropFilter: 'blur(10px)',
                            color: 'white',
                            padding: '8px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.3)'
                          }}>
                            <span style={{ fontSize: '14px' }}>🎵</span>
                            {videoCount} videos
                          </div>
                        </div>
                      ) : (
                        // No thumbnails - show artistic folder icon
                        <div style={{ 
                          textAlign: 'center', 
                          color: 'rgba(255,255,255,0.8)',
                          background: 'transparent'
                        }}>
                          <div style={{ 
                            fontSize: '4rem', 
                            marginBottom: '0.5rem',
                            filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))'
                          }}>
                            📁
                          </div>
                          <div style={{ 
                            fontSize: '1rem', 
                            fontWeight: '600',
                            textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                          }}>
                            {videoCount} videos
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Card content with improved styling */}
                    <div className="card-body d-flex flex-column" style={{ 
                      padding: '20px',
                      background: 'white'
                    }}>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h5 className="card-title flex-grow-1" style={{ 
                          fontSize: '1.2rem', 
                          fontWeight: '700',
                          lineHeight: '1.3',
                          marginBottom: '0.5rem',
                          color: '#2c3e50'
                        }}>
                          {playlist.name}
                        </h5>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={(e) => handleDeletePlaylist(playlist.id, e)}
                          title="Delete Playlist"
                          style={{
                            marginLeft: '12px',
                            minWidth: '32px',
                            height: '32px',
                            padding: '0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '16px',
                            lineHeight: '1',
                            borderRadius: '50%',
                            transition: 'all 0.2s ease',
                            border: '2px solid #dc3545'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'scale(1.1)';
                            e.target.style.backgroundColor = '#dc3545';
                            e.target.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'scale(1)';
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.color = '#dc3545';
                          }}
                        >
                          ×
                        </button>
                      </div>
                      
                      {playlist.description && (
                        <p className="card-text text-muted mb-3" style={{ 
                          fontSize: '0.9rem',
                          lineHeight: '1.5',
                          color: '#6c757d'
                        }}>
                          {playlist.description.length > 100 
                            ? `${playlist.description.substring(0, 100)}...` 
                            : playlist.description
                          }
                        </p>
                      )}
                      
                      <div className="mt-auto">
                        <div className="d-flex justify-content-between align-items-center" style={{
                          padding: '12px 0',
                          borderTop: '1px solid #e9ecef'
                        }}>
                          <small className="text-muted" style={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            fontSize: '0.8rem',
                            fontWeight: '500'
                          }}>
                            <span style={{ 
                              marginRight: '6px', 
                              fontSize: '16px',
                              filter: 'grayscale(0.3)'
                            }}>
                              🎬
                            </span>
                            {videoCount} videos
                          </small>
                          <small className="text-muted" style={{ 
                            fontSize: '0.8rem',
                            fontWeight: '500'
                          }}>
                            {formatDate(playlist.savedAt)}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      <br />
      <br />
      
      <style jsx>{`
        @media (max-width: 576px) {
          .playlist-gallery-card .card-body {
            padding: 16px !important;
          }
          
          .playlist-gallery-card h5 {
            font-size: 1.1rem !important;
          }
          
          .playlist-gallery-card .card-text {
            font-size: 0.85rem !important;
          }
        }
        
        @media (max-width: 768px) {
          .playlist-gallery-card {
            margin-bottom: 2rem;
          }
        }
        
        .playlist-gallery-card:hover .card-title {
          color: #007bff !important;
        }
        
        .playlist-gallery-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        
        .playlist-gallery-card:hover::before {
          opacity: 1;
        }
      `}</style>
    </div>
  );
};

export default SavedPlaylists;